<template>
  <div>
    <slot />
  </div>
</template>

<script lang="ts">
/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return */
/* TODO: Add proper typing for Flickity library integration */
import {
  Component,
  Emit,
  Prop,
  toNative,
  Vue,
  Watch,
} from '~/utility/pu-class-decorator'

@Component
class Puflickity extends Vue {
  @Prop()
  options: Object

  @Prop({ default: true })
  start: boolean

  hasDoneInit = false

  flickityInstance: any = null

  async mounted() {
    if (this.start) {
      await this.$nextTick()
      await this.initFlickity()
    }
  }

  unmounted() {
    if (this) {
      const flickityInstance = this.flickityInstance
      this.flickityInstance = null
      requestIdleCallback(() => {
        if (flickityInstance) {
          flickityInstance.destroy()
        }
      })
    }
  }

  render() {
    return h('div', {}, this.$slots.default?.())
  }

  @Watch('start')
  async initChanged(start: boolean) {
    if (start) {
      await this.$nextTick()
      await this.initFlickity()
    }
  }

  /**
   * Initialize a new flickity and emit init event.
   */
  @Emit('init')
  emitInit() {
    return this.flickityInstance
  }

  async initFlickity() {
    if (this.hasDoneInit) {
      return
    }
    this.hasDoneInit = true

    await Promise.all([
      this.importFlickityFullscreen(),
      this.importFlickity(),
    ]).then(([_, Flickity]) => {
      this.flickityInstance = new Flickity(this.$el, this.options)
      this.emitInit()
    })
  }

  importFlickity() {
    if (process.client) {
      return import(
        /* webpackChunkName: "flickity", webpackMode: "lazy" */ 'flickity'
      ).then((Flickity) => Flickity.default || Flickity)
    } else {
      return Promise.resolve(null)
    }
  }

  importFlickityFullscreen() {
    if (process.client) {
      return import(
        /* webpackChunkName: "flickity-fullscreen", webpackMode: "lazy" */ 'flickity-fullscreen'
      )
    } else {
      return Promise.resolve(null)
    }
  }

  /**
   * Return the current flickity instance to access directly
   *
   * @return {Flickity}
   */
  flickity() {
    return this.flickityInstance
  }

  /**
   * Selecting Slides
   */

  /**
   * Select a slide
   *
   * @param {number} index
   * @param {boolean} isWrapped
   * @param {boolean} isInstant
   */
  select(index, isWrapped, isInstant) {
    this.flickityInstance.select(index, isWrapped, isInstant)
  }

  /**
   * Change to the next slide
   *
   * @param {boolean} isWrapped
   * @param {boolean} isInstant
   */
  next(isWrapped, isInstant) {
    this.flickityInstance.next(isWrapped, isInstant)
  }

  /**
   * Change to the previous slide
   *
   * @param {boolean} isWrapped
   * @param {boolean} isInstant
   */
  previous(isWrapped, isInstant) {
    this.flickityInstance.previous(isWrapped, isInstant)
  }

  /**
   * Select a cell
   *
   * @param {number} value
   * @param {boolean} isWrapped
   * @param {boolean} isInstant
   */
  selectCell(value, isWrapped, isInstant) {
    this.flickityInstance.selectCell(value, isWrapped, isInstant)
  }

  /**
   * Sizing and Positioning
   */

  /**
   * Trigger a resize event
   */
  resize() {
    this.flickityInstance.resize()
  }

  /**
   * Trigger a reposition event
   */
  reposition() {
    this.flickityInstance.reposition()
  }

  /**
   * Adding and removing cells
   */

  /**
   * Prepend elements to flickity
   *
   * @param {array|HTMLElement|NodeList} elements
   */
  prepend(elements) {
    this.flickityInstance.prepend(elements)
  }

  /**
   * Append elements to flickity
   *
   * @param {array|HTMLElement|NodeList} elements
   */
  append(elements) {
    this.flickityInstance.append(elements)
  }

  /**
   * Insert elements at a given index
   *
   * @param {array|HTMLElement|NodeList} elements
   * @param {number} index
   */
  insert(elements, index) {
    this.flickityInstance.insert(elements, index)
  }

  /**
   * Remove elements from flickity
   *
   * @param {array|HTMLElement|NodeList} elements
   */
  remove(elements) {
    this.flickityInstance.remove(elements)
  }

  /**
   * Player
   */

  /**
   * Trigger a playPlayer event
   */
  playPlayer() {
    this.flickityInstance.playPlayer()
  }

  /**
   * Trigger a stopPlayer event
   */
  stopPlayer() {
    this.flickityInstance.stopPlayer()
  }

  /**
   * Trigger a pausePlayer event
   */
  pausePlayer() {
    this.flickityInstance.pausePlayer()
  }

  /**
   * Trigger a unpausePlayer event
   */
  unpausePlayer() {
    this.flickityInstance.unpausePlayer()
  }

  /**
   * Trigger a rerender event
   */
  async rerender() {
    this.flickityInstance.destroy()
    this.hasDoneInit = false
    await this.initFlickity()
  }

  /**
   * Utilities
   */

  /**
   * Destroy the flickity instance
   */
  destroy() {
    this.flickityInstance.destroy()
  }

  /**
   * Trigger a rerender event
   */
  reloadCells() {
    this.flickityInstance.reloadCells()
  }

  /**
   * Get the cell elements
   *
   * @return {array}
   */
  getCellElements() {
    return this.flickityInstance.getCellElements()
  }

  // /**
  //  * Return flickity data
  //  *
  //  * @return {Flickity}
  //  */
  // data() {
  //   const Flickity = require('flickity');

  //   return Flickity.data(this.$el);
  // }

  /**
   * Events
   */

  /**
   * Attach an event
   *
   * @param {string} eventName
   * @param {function} listener
   */
  on(eventName, listener) {
    this.flickityInstance.on(eventName, listener)
  }

  /**
   * Remove an event
   *
   * @param {string} eventName
   * @param {function} listener
   */
  off(eventName, listener) {
    this.flickityInstance.off(eventName, listener)
  }

  /**
   * Attach an event once
   *
   * @param {string} eventName
   * @param {function} listener
   */
  once(eventName, listener) {
    this.flickityInstance.once(eventName, listener)
  }

  /**
   * Properties
   */

  /**
   * Return the selected element
   *
   * @return {HTMLElement}
   */
  selectedElement() {
    return this.flickityInstance.selectedElement
  }

  /**
   * Return the selected elements
   *
   * @return {array}
   */
  selectedElements() {
    return this.flickityInstance.selectedElements
  }

  /**
   * Return the selected index
   *
   * @return {number}
   */
  selectedIndex() {
    return this.flickityInstance.selectedIndex
  }

  /**
   * Return the cells
   *
   * @return {array}
   */
  cells() {
    return this.flickityInstance.cells
  }

  /**
   * Return the slides
   *
   * @return {array}
   */
  slides() {
    return this.flickityInstance.slides
  }

  /**
   * Disable dragging of slider
   */
  disableDrag() {
    this.flickityInstance.options.draggable = false
    this.flickityInstance.updateDraggable()
  }

  /**
   * Enable dragging of slider
   */
  enableDrag() {
    this.flickityInstance.options.draggable = true
    this.flickityInstance.updateDraggable()
  }
}

export default toNative(Puflickity)
</script>
